import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { object, string, ref } from "yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { InputAdornment, IconButton } from "@mui/material";
import WDButton from "../../../MaterialComponents/WDButton";
import { useEffect, useState } from "react";
import AppService from "../../../Configurations/Appservice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  dismissLoader,
  showLoader,
  showSuccessMessage,
  updateScreenTitle,
} from "../../../LayoutContainers/AppLayout/redux/AppSlice";

const initialValues = {
  paymentMethodId: "",
  comment: "",
  bankName: "",
  accountNumber: "",
  confirmAccountNumber: "",
  iban: "",
  branch: "",
};

const getValidationSchema = (paymentMethodId) => {
  let schema = object({
    paymentMethodId: string().required("Payment Method is Mandatory"),
  });

  // Add conditional validations based on payment method type
  if (paymentMethodId === "BANK_TRANSFER") {
    schema = schema.shape({
      bankName: string().required("Bank Name is required for Bank Transfer"),
      accountNumber: string().required("Account Number is required"),
      confirmAccountNumber: string()
        .required("Please confirm your account number")
        .oneOf([ref('accountNumber')], 'Account numbers must match'),
    });
  } else if (paymentMethodId === "CHEQUE") {
    schema = schema.shape({
      bankName: string().required("Bank Name is required for Cheque"),
    });
  } else if (paymentMethodId === "CASH_DEPOSIT") {
    schema = schema.shape({
      bankName: string().required("Bank Name is required for Cash Deposit"),
    });
  }

  return schema;
};

const CustomerPaymentMethod = (props) => {
  const [paymentMehods, setPaymentMethods] = useState();
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    AppService.get("v1/paymentMethod/keyValues")
      .then((response) => {
        setPaymentMethods(response);
      })
      .catch(() => {})
      .finally(() => {});
  }, []);

  useEffect(() => {
    dispatch(updateScreenTitle("New Customer Payment Method"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  const _onSubmit = (values, { resetForm }) => {
    dispatch(showLoader());
    AppService.post("v1/customerPaymentMethod", values)
      .then((response) => {
        resetForm();
        dispatch(showSuccessMessage("New method added successfully"));
        // Redirect to payment methods list after 1.5 seconds
        setTimeout(() => {
          navigate("/wallet/payment_methods");
        }, 1500);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  return (
    <Box>
      <Card sx={{ padding: 3 }}>
        <Box pb={3}>
          <Typography fontSize={16}>Add New Payment Method</Typography>
        </Box>
        <Formik
          initialValues={initialValues}
          onSubmit={_onSubmit}
          validationSchema={(values) => {
            if (!values) return object({});
            return getValidationSchema(values.paymentMethodId || '');
          }}
        >
          {({ errors, isValid, touched, dirty, setFieldValue, values }) => (
            <Form>
              <Grid container spacing={2}>
                {/* Payment Method Type */}
                <Grid item xs={6}>
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel id="method-label">Payment Method Type</InputLabel>
                      <Field
                        name="paymentMethodId"
                        label="Payment Method Type"
                        labelId="method-label"
                        size="small"
                        fullWidth
                        as={Select}
                        onChange={(e) => {
                          setFieldValue("paymentMethodId", e.target.value);
                          // Clear type-specific fields when changing method
                          if (e.target.value !== "BANK_TRANSFER") {
                            setFieldValue("bankName", "");
                            setFieldValue("accountNumber", "");
                            setFieldValue("iban", "");
                          }
                        }}
                        error={Boolean(errors.paymentMethodId) && Boolean(touched.paymentMethodId)}
                      >
                        {paymentMehods ? (
                          paymentMehods.map((method) => (
                            <MenuItem key={method.id} value={method.id}>
                              {method.name}
                            </MenuItem>
                          ))
                        ) : (
                          <Typography fontSize={14}>Loading...</Typography>
                        )}
                      </Field>
                    </FormControl>
                  </Box>
                </Grid>

                {/* Bank Transfer Fields */}
                {values.paymentMethodId === "BANK_TRANSFER" && (
                  <>
                    <Grid item xs={6}>
                      <Field
                        name="bankName"
                        label="Bank Name"
                        fullWidth
                        as={TextField}
                        size="small"
                        placeholder="e.g., HDFC Bank"
                        error={Boolean(errors.bankName) && Boolean(touched.bankName)}
                        helperText={Boolean(touched.bankName) && errors.bankName}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        name="accountNumber"
                        label="Account Number"
                        fullWidth
                        as={TextField}
                        size="small"
                        type={showAccountNumber ? "text" : "password"}
                        placeholder="e.g., 1234567890"
                        error={Boolean(errors.accountNumber) && Boolean(touched.accountNumber)}
                        helperText={Boolean(touched.accountNumber) && errors.accountNumber}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowAccountNumber(!showAccountNumber)}
                                edge="end"
                                size="small"
                              >
                                {showAccountNumber ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        name="confirmAccountNumber">
                        {({ field }) => (
                          <TextField
                            {...field}
                            label="Confirm Account Number"
                            fullWidth
                            size="small"
                            type="password"
                            placeholder="Re-enter account number"
                            error={(values.confirmAccountNumber && values.accountNumber !== values.confirmAccountNumber) || (Boolean(errors.confirmAccountNumber) && Boolean(touched.confirmAccountNumber))}
                            helperText={
                              values.confirmAccountNumber && values.accountNumber !== values.confirmAccountNumber
                                ? 'Account numbers do not match'
                                : (Boolean(touched.confirmAccountNumber) && errors.confirmAccountNumber)
                            }
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        name="iban"
                        label="IBAN (Optional)"
                        fullWidth
                        as={TextField}
                        size="small"
                        placeholder="e.g., AE070331234567890123456"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        name="branch"
                        label="Branch"
                        fullWidth
                        as={TextField}
                        size="small"
                        placeholder="e.g., Downtown Dubai"
                      />
                    </Grid>
                  </>
                )}

                {/* Cheque Fields */}
                {values.paymentMethodId === "CHEQUE" && (
                  <Grid item xs={6}>
                    <Field
                      name="bankName"
                      label="Bank Name"
                      fullWidth
                      as={TextField}
                      size="small"
                      placeholder="e.g., Emirates Bank"
                      error={Boolean(errors.bankName) && Boolean(touched.bankName)}
                      helperText={Boolean(touched.bankName) && errors.bankName}
                    />
                  </Grid>
                )}

                {/* Cash Deposit Fields */}
                {values.paymentMethodId === "CASH_DEPOSIT" && (
                  <Grid item xs={6}>
                    <Field
                      name="bankName"
                      label="Bank Name"
                      fullWidth
                      as={TextField}
                      size="small"
                      placeholder="e.g., ADIB"
                      error={Boolean(errors.bankName) && Boolean(touched.bankName)}
                      helperText={Boolean(touched.bankName) && errors.bankName}
                    />
                  </Grid>
                )}

                {/* Description/Notes - Always show */}
                <Grid item xs={12}>
                  <Field
                    name="comment"
                    label="Additional Notes/Description"
                    fullWidth
                    as={TextField}
                    size="small"
                    multiline
                    minRows={3}
                    placeholder="e.g., Primary account, Account holder: John Doe"
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" pt={3}>
                <WDButton 
                  variant="gradient" 
                  disabled={!dirty || !isValid || (values.paymentMethodId === "BANK_TRANSFER" && values.accountNumber !== values.confirmAccountNumber)} 
                  type="submit"
                >
                  Add Payment Method
                </WDButton>
              </Box>
            </Form>
          )}
        </Formik>
      </Card>
    </Box>
  );
};

export default CustomerPaymentMethod;
